import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, ClockIcon, MailIcon, PhoneIcon, ShieldCheckIcon } from 'lucide-react';
import { getServiceBySlug, services } from '../data/services';
import { contact, processSteps } from '../data/site';
import { Icon } from '../utils/icons';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
export function ServiceDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const service = getServiceBySlug(slug);
  if (!service) {
    return <Navigate to="/services" replace data-id="element-224" data-mp="17rpu9fif538y" />;
  }
  const related = services.filter(item => item.slug !== service.slug).slice(0, 3);
  return <>
      <PageHero eyebrow="Our Services" title={service.title} description={service.tagline} image={service.image} breadcrumb={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Services',
      to: '/services'
    }, {
      label: service.shortTitle
    }]} data-id="element-225" data-mp="3sj9v7a64qn7b" />

      <section className="w-full bg-white" data-id="element-226" data-mp="1dgcmat8hjeko">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24" data-id="element-227" data-mp="1dfb84fw6abyq">
          <div className="grid gap-14 lg:grid-cols-[1.6fr_1fr]" data-id="element-228" data-mp="zf9zufdkw298">
            <div data-id="element-229" data-mp="13hn3my8rlq3d">
              <img src={service.image} alt={service.title} className="h-72 w-full object-cover lg:h-[420px]" data-id="element-230" data-mp="13h5awblb9u07" />

              <p className="mt-10 text-[18px] leading-relaxed text-gray-700" data-id="element-231" data-mp="uh8grug5k2xr">
                {service.intro}
              </p>

              <h2 className="mt-12 font-display text-2xl font-extrabold uppercase tracking-tight text-navy" data-id="element-232" data-mp="ft3xao99zndx">
                What's Included
              </h2>
              <span className="mt-4 block h-[3px] w-16 bg-gold" aria-hidden="true" data-id="element-233" data-mp="kp2nmhe5zfgz" />
              <ul className="mt-7 grid gap-4 sm:grid-cols-2" data-id="element-234" data-mp="6tqvmhre833r">
                {service.includes.map(item => <li key={item} className="flex items-start gap-3" data-id="element-235" data-mp="bsk23q89ituf">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold" data-id="element-236" data-mp="2dh7oylqr5r6e">
                      <CheckIcon className="h-3.5 w-3.5 text-white" aria-hidden="true" data-id="element-237" data-mp="28n3kc94ylv8h" />
                    </span>
                    <span className="text-[15px] leading-relaxed text-gray-700" data-id="element-238" data-mp="1zoi44kmymsav">
                      {item}
                    </span>
                  </li>)}
              </ul>

              <h2 className="mt-14 font-display text-2xl font-extrabold uppercase tracking-tight text-navy" data-id="element-239" data-mp="2rfo676m8r02s">
                How It Works
              </h2>
              <span className="mt-4 block h-[3px] w-16 bg-gold" aria-hidden="true" data-id="element-240" data-mp="2ncj598ht76g9" />
              <ol className="mt-7 space-y-6" data-id="element-241" data-mp="2idy67ceamste">
                {processSteps.map((step, index) => <li key={step.title} className="flex gap-5" data-id="element-242" data-mp="1bzywm7bqijju" data-mpi={index}>
                    <span className="font-display text-2xl font-black text-gold/50" data-id="element-243" data-mp="17041frkxf7sz">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div data-id="element-244" data-mp="3il39y3owc7c2">
                      <h3 className="font-display text-base font-extrabold uppercase tracking-wide text-navy" data-id="element-245" data-mp="3nm0z508bq1n2">
                        {step.title}
                      </h3>
                      <p className="mt-1.5 text-[15px] leading-relaxed text-gray-600" data-id="element-246" data-mp="3rm1dylzc4eun">
                        {step.description}
                      </p>
                    </div>
                  </li>)}
              </ol>

              <h2 className="mt-14 font-display text-2xl font-extrabold uppercase tracking-tight text-navy" data-id="element-247" data-mp="a6l6ygdtbhzb">
                Frequently Asked Questions
              </h2>
              <span className="mt-4 block h-[3px] w-16 bg-gold" aria-hidden="true" data-id="element-248" data-mp="j82idlg9yvho" />
              <dl className="mt-7 divide-y divide-gray-200 border-y border-gray-200" data-id="element-249" data-mp="j5yeh3idlwhe">
                {service.faqs.map(faq => <div key={faq.question} className="py-6" data-id="element-250" data-mp="2kyw5ctsleezp">
                    <dt className="font-display text-base font-extrabold uppercase tracking-wide text-navy" data-id="element-251" data-mp="2l1icwtfhguhx">
                      {faq.question}
                    </dt>
                    <dd className="mt-2 text-[15px] leading-relaxed text-gray-600" data-id="element-252" data-mp="1lmw07j9lpdfl">
                      {faq.answer}
                    </dd>
                  </div>)}
              </dl>
            </div>

            <aside className="lg:sticky lg:top-32 lg:self-start" data-id="element-253" data-mp="1um98i8lznvyq">
              <div className="bg-navy p-8 text-white" data-id="element-254" data-mp="16zsx8x2ieckv">
                <p className="font-display text-[12px] font-bold uppercase tracking-[0.28em] text-gold" data-id="element-255" data-mp="11zojvfwwdxyv">
                  Free Quote
                </p>
                <h2 className="mt-3 font-display text-xl font-extrabold uppercase tracking-wide" data-id="element-256" data-mp="1gq5i6qe8k7rj">
                  Book {service.shortTitle}
                </h2>
                <p className="mt-3 text-[15px] leading-relaxed text-white/70" data-id="element-257" data-mp="o7qdvdyhgi6x">
                  Tell us about the property and your deadline — we'll come back with a
                  clear, no-obligation price.
                </p>
                <ul className="mt-6 space-y-3 text-[15px] text-white/80" data-id="element-258" data-mp="2uvqule69hmuz">
                  <li data-id="element-259" data-mp="2zniiti1y3ghy">
                    <a href={contact.phoneHref} className="flex items-center gap-2 transition-colors hover:text-gold-light" data-id="element-260" data-mp="2zo0mv0uumz0f">
                      <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-261" data-mp="2py8so7qnw7wh" />
                      {contact.phone}
                    </a>
                  </li>
                  <li data-id="element-262" data-mp="3im4ozbcbjxsq">
                    <a href={`mailto:${contact.email}`} className="flex items-center gap-2 transition-colors hover:text-gold-light" data-id="element-263" data-mp="3imd1h24j513e">
                      <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-264" data-mp="39kn7ppn6dq2g" />
                      {contact.email}
                    </a>
                  </li>
                  <li className="flex items-center gap-2" data-id="element-265" data-mp="1t9ja0f77nb9g">
                    <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-266" data-mp="1y71adh75wi5u" />
                    {contact.hours}
                  </li>
                  <li className="flex items-center gap-2" data-id="element-267" data-mp="3s5k8xq376xo">
                    <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-268" data-mp="3uxdroe6e3r5e" />
                    Fully insured &amp; 10 years' experience
                  </li>
                </ul>
                <div className="mt-7 flex flex-col gap-3" data-id="element-269" data-mp="3m4k8w6rqi9ms">
                  <a href={contact.phoneHref} className="flex items-center justify-center gap-3 bg-gold px-7 py-4 font-display text-[12px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-gold-dark" data-id="element-270" data-mp="3m1y0y60xls41">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-271" data-mp="3771mvc6fyaj1" />
                    Call {contact.phone}
                  </a>
                  <Link to="/#contact" className="block border border-white/40 px-7 py-4 text-center font-display text-[12px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-navy" data-id="element-272" data-mp="15fetx52zo5jx">
                    Contact Us
                  </Link>
                </div>
              </div>

              <div className="mt-6 border border-gray-200 p-8" data-id="element-273" data-mp="2o8exzpa1tpyc">
                <h2 className="font-display text-[12px] font-bold uppercase tracking-[0.28em] text-gold" data-id="element-274" data-mp="2t2j1fyk3vfuo">
                  Ideal For
                </h2>
                <ul className="mt-4 flex flex-wrap gap-2" data-id="element-275" data-mp="20cu0fl7ebft2">
                  {service.idealFor.map(item => <li key={item} className="border border-gray-300 px-3 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider text-navy" data-id="element-276" data-mp="25b5bfz9ffwcv">
                      {item}
                    </li>)}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="w-full bg-gray-50" data-id="element-277" data-mp="3pqz5t0rvjtru">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24" data-id="element-278" data-mp="3ppxkm3h59qwe">
          <SectionHeading eyebrow="You Might Also Need" title="Other Services" data-id="element-279" data-mp="31e5caiqgt1fr" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-280" data-mp="2wg3sfvl7sy38">
            {related.map(item => <motion.div key={item.slug} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4
          }} data-id="element-281" data-mp="3a7ib21b7mx9r">
                <Link to={`/services/${item.slug}`} className="group flex h-full flex-col border border-gray-200 bg-white p-8 transition-colors hover:border-gold" data-id="element-282" data-mp="3f6lghmv3r5i9">
                  <span className="flex h-11 w-11 items-center justify-center bg-navy transition-colors group-hover:bg-gold" data-id="element-283" data-mp="3p49ceg4g2j20">
                    <Icon name={item.icon} className="h-5 w-5 text-white" data-id="element-284" data-mp="1yslkgmzdo00a" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-extrabold uppercase tracking-wide text-navy" data-id="element-285" data-mp="1us2p4iya0btc">
                    {item.title}
                  </h3>
                  <p className="mt-3 flex-1 text-[15px] leading-relaxed text-gray-600" data-id="element-286" data-mp="1ooofiqd4s710">
                    {item.description}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 font-display text-[12px] font-bold uppercase tracking-widest text-gold" data-id="element-287" data-mp="1awhzd49za01k">
                    Learn More
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-288" data-mp="10xsrm4bspvz1" />
                  </span>
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>
    </>;
}